/**
 * What can go wrong with the save on the device, as typed errors the UI can tell apart (FINISH_PLAN WP4.1, X4). The
 * durable save store (durableSaveStore.ts) never throws them at the game: a save call must not break a battle. It
 * reports them to its listeners, and features/platform.ts turns each into a notice.
 */

/** Where a stored copy lives: the durable backend (IndexedDB on the web, Preferences in the iOS shell) or the localStorage mirror. */
export type SaveCopy = "backend" | "mirror";

/** A stored save that is not what was written (a failed checksum, unreadable JSON). A copy of it is kept. */
export class SaveCorruptError extends Error {
  readonly kind = "corrupt";
  constructor(readonly copy: SaveCopy | "record", readonly backupKey: string, detail: string) {
    super(`save copy in ${copy} is corrupt (${detail}); kept under ${backupKey}`);
    this.name = "SaveCorruptError";
  }
}

/** The device refused the write for space (QuotaExceededError). The last good save stays. */
export class SaveQuotaError extends Error {
  readonly kind = "quota";
  constructor(readonly copy: SaveCopy, readonly bytes: number) {
    super(`storage full: the ${copy} refused ${bytes} characters`);
    this.name = "SaveQuotaError";
  }
}

/** The save grew past the size guard; `shed` names what was dropped to fit, or null when nothing could be. */
export class SaveTooLargeError extends Error {
  readonly kind = "tooLarge";
  constructor(readonly chars: number, readonly limit: number, readonly shed: string | null) {
    super(`save is ${chars} characters, over the ${limit} limit${shed ? `; dropped ${shed}` : "; not written"}`);
    this.name = "SaveTooLargeError";
  }
}

/** Another tab or window took the save over (single-writer lock); this one no longer writes. */
export class SaveLockedError extends Error {
  readonly kind = "locked";
  constructor() {
    super("the game is open in another tab: this one stopped saving");
    this.name = "SaveLockedError";
  }
}

/** A write to the backend failed for a reason other than space (IndexedDB closed by the OS, a private window). */
export class SaveBackendError extends Error {
  readonly kind = "backend";
  constructor(detail: string) {
    super(`save backend write failed: ${detail}`);
    this.name = "SaveBackendError";
  }
}

export type SaveProblem = SaveCorruptError | SaveQuotaError | SaveTooLargeError | SaveLockedError | SaveBackendError;

/** True for the DOMException browsers throw when storage is full (Safari, Chromium and Firefox name it differently). */
export function isQuotaError(err: unknown): boolean {
  const e = err as { name?: string; code?: number } | null;
  // Legacy codes: 22 (WebKit, Chromium), 1014 (old Firefox).
  const QuotaCodes = [22, 1014];
  return !!e && (e.name === "QuotaExceededError" || e.name === "NS_ERROR_DOM_QUOTA_REACHED" || QuotaCodes.includes(e.code ?? -1));
}
